import { LANGUAGES } from "@translations/next";

const LANGUAGE_STORAGE_KEY = "amanaki_preferred_language";

/**
 * Get browser language and map it to supported language codes
 */
export const detectBrowserLanguage = (): string => {
  if (typeof window === "undefined") {
    return "en"; // Server-side fallback
  }

  // Get browser language
  const browserLang = navigator.language || (navigator as any).userLanguage || "en";
  
  // Map browser language to our supported languages
  // Check full locale first (e.g., "zh-CN" -> "zh")
  if (LANGUAGES[browserLang as keyof typeof LANGUAGES]) {
    return LANGUAGES[browserLang as keyof typeof LANGUAGES];
  }
  
  // Check language code only (e.g., "zh" from "zh-CN")
  const langCode = browserLang.split("-")[0].toLowerCase();
  
  // Special handling for Chinese variants
  if (langCode === "zh") {
    // Check if it's Hong Kong or Macau (Cantonese regions)
    const region = browserLang.split("-")[1]?.toUpperCase();
    if (region === "HK" || region === "MO") {
      return "zh-HK";
    }
    // Default to Simplified Chinese (Mainland China)
    return "zh";
  }
  
  const supportedLanguages = ["en", "vi", "zh", "zh-HK", "ko", "ja"];
  
  if (supportedLanguages.includes(langCode)) {
    return langCode;
  }
  
  // Fallback to English
  return "en";
};

/**
 * Get preferred language from localStorage or browser
 */
export const getPreferredLanguage = (): string => {
  if (typeof window === "undefined") {
    return "en"; // Server-side fallback
  }

  try {
    // Check localStorage first
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      const supportedLanguages = ["en", "vi", "zh", "zh-HK", "ko", "ja"];
      if (supportedLanguages.includes(parsed)) {
        return parsed;
      }
    }
  } catch (err) {
    console.warn("Failed to read language from localStorage:", err);
  }

  // Fallback to browser language detection
  return detectBrowserLanguage();
};

/**
 * Save preferred language to localStorage
 */
export const savePreferredLanguage = (language: string): void => {
  if (typeof window === "undefined") {
    return; // Server-side, do nothing
  }

  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, JSON.stringify(language));
  } catch (err) {
    console.warn("Failed to save language to localStorage:", err);
  }
};

